/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import Clarity from '@microsoft/clarity';
import { environment } from '../../../environments/environment';

import { AuthAppService } from './auth-app/auth-app.service';
import { Constants } from './constants';

@Injectable({
  providedIn: 'root'
})
export class ClarityService {
  private clarityLoaded = false;
  private clarityProjectId = Constants.CLARITY_PROJECT_ID;

  constructor(private authService: AuthAppService) {}

  /**
   * Initialize Microsoft Clarity with the project ID
   */
  initClarity(): void {
    // Only run Clarity on production
    if (!environment.production) {
      console.log('🚫 Clarity disabled in development environment');
      return;
    }

    if (this.clarityLoaded || typeof document === 'undefined' || !this.clarityProjectId) {
      if (!this.clarityProjectId) {
        console.warn('Microsoft Clarity project ID not configured');
      }
      return;
    }

    // Microsoft Clarity tracking code
    Clarity.init(this.clarityProjectId);
    Clarity.setTag('APP_NAME', environment.appName);
    this.clarityLoaded = true;
  }

  /**
   * Track custom events in Clarity
   */
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    // Only track events on production
    if (!environment.production) {
      console.log('🚫 Clarity event skipped (development):', eventName, properties);
      return;
    }

    if (!this.clarityLoaded) {
      console.warn('Clarity not loaded, cannot track event:', eventName);
      return;
    }

    Clarity.event(eventName);
    if (properties) {
      Object.entries(properties).forEach(([key, value]) => {
        Clarity.setTag(`event_${key}`, String(value));
      });
    }
  }

  /**
   * Set custom session tags
   */
  setCustomTag(key: string, value: string): void {
    // Only set tags on production
    if (!environment.production) {
      return;
    }

    if (!this.clarityLoaded) {
      return;
    }

    Clarity.setTag(key, value);
  }

  /**
   * Upgrade session to capture full recording (useful for important user flows)
   */
  upgradeSession(): void {
    // Only upgrade sessions on production
    if (!environment.production) {
      console.log('🚫 Clarity session upgrade skipped (development)');
      return;
    }

    if (!this.clarityLoaded) {
      console.warn('Clarity not loaded, cannot upgrade session');
      return;
    }

    Clarity.upgrade('');
  }

  /**
   * Test function to verify Clarity is working
   */
  testClarity(): void {
    console.log('🧪 Testing Clarity integration...');
    console.log('Environment:', environment.production ? 'Production' : 'Development');
    console.log('Clarity loaded:', this.clarityLoaded);
    console.log('Clarity package available:', !!Clarity);
    console.log('Project ID:', this.clarityProjectId);

    if (!environment.production) {
      console.log('⚠️ Clarity is disabled in development environment');
      console.log('💡 Deploy to production to see actual Clarity tracking');
      return;
    }

    if (this.clarityLoaded) {
      this.trackEvent('clarity_test', {
        test_timestamp: new Date().toISOString(),
        test_source: 'manual_test'
      });
      console.log('✅ Test event sent to Clarity');
    } else {
      console.log('❌ Clarity not ready for testing');
    }
  }
}
